import { DateTime } from "luxon";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { BookableBookingObjectDto } from "../../../../features/Schedule/typings/resource-calendar.types";

export function mergeColleagueScheduleSources(
  placeScheduleData: BookingScheduleInterface[] | undefined,
  noPlaceZoneBookingScheduleData: BookingScheduleInterface[] | undefined,
  vehicleScheduleData: BookingScheduleInterface[] | undefined,
  resourceScheduleData?: BookingScheduleInterface[] | undefined
): BookingScheduleInterface[] {
  const place = placeScheduleData ?? [];
  const noPlace = noPlaceZoneBookingScheduleData ?? [];
  const vehicle = vehicleScheduleData ?? [];
  const resource = resourceScheduleData ?? [];
  return [...place, ...noPlace, ...vehicle, ...resource];
}

export function dedupeBookableBookingObjects(
  objects: BookableBookingObjectDto[]
): BookableBookingObjectDto[] {
  const seen = new Set<string>();
  return objects.filter(obj => {
    const key = `${obj.bookingType}\0${obj.bookingInventoryId}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/** Inclusive local-day bounds for API fetch and client-side overlap checks. */
export function normalizeExportPeriodBounds(period: { startDate: string; endDate: string }): {
  rangeStart: DateTime;
  rangeEnd: DateTime;
  fetchStart: string;
  fetchEnd: string;
} {
  const rangeStart = DateTime.fromISO(period.startDate).toLocal().startOf("day");
  const rangeEnd = DateTime.fromISO(period.endDate).toLocal().endOf("day");
  return {
    rangeStart,
    rangeEnd,
    fetchStart: rangeStart.toISO() ?? period.startDate,
    fetchEnd: rangeEnd.toISO() ?? period.endDate
  };
}

export function scheduleIntersectsRange(
  schedule: BookingScheduleInterface,
  rangeStart: DateTime,
  rangeEnd: DateTime
): boolean {
  const s0 = DateTime.fromISO(schedule.startDate);
  const s1 = DateTime.fromISO(schedule.endDate);
  return s0 <= rangeEnd && s1 >= rangeStart;
}

export function defaultExportPeriodIso(): { start: string; end: string } {
  const start = DateTime.now().startOf("day");
  const end = start.plus({ days: 6 }).endOf("day");
  return { start: start.toISO() ?? "", end: end.toISO() ?? "" };
}

function cellToCsvString(cell: unknown): string {
  if (cell == null) {
    return "";
  }
  if (typeof cell === "string") {
    return cell;
  }
  if (typeof cell === "number" || typeof cell === "boolean") {
    return String(cell);
  }
  return "";
}

function escapeCsvCell(cell: unknown, separator: string): string {
  const s = cellToCsvString(cell);
  if (s.includes('"') || s.includes(separator) || s.includes("\n") || s.includes("\r")) {
    return `"${s.replaceAll('"', '""')}"`;
  }
  return s;
}

export function triggerCsvDownload(
  headers: string[],
  rows: unknown[][],
  separator: string,
  baseFilename: string
): void {
  const headerLine = headers.map(h => escapeCsvCell(h, separator)).join(separator);
  const body = rows.map(r => r.map(c => escapeCsvCell(c, separator)).join(separator)).join("\n");
  const bom = "\uFEFF";
  const blob = new Blob([bom + headerLine + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const safeName = baseFilename.endsWith(".csv") ? baseFilename : `${baseFilename}.csv`;
  anchor.download = safeName;
  anchor.click();
  URL.revokeObjectURL(url);
}
